// EmojiPicker (DESIGN §3.17): Popover 320 × 360 mit Suche, „Frequently used“ und Raster aus
// 8 Spalten à 36. Tastatur: ↓ aus der Suche ins Raster, Pfeile/Home/End im Raster (Roving),
// Enter/Leertaste wählt, ↑ in der ersten Zeile zurück zur Suche. Emojis nur als Reaktion und im
// Chat-Text, nie in UI-Texten.
import { useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { SmileyIcon } from '@phosphor-icons/react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import {
  DEFAULT_FREQUENT,
  EMOJI_GROUPS,
  emojiByChar,
  gridMove,
  searchEmojis,
  type EmojiEntry,
} from './_lib/emojiData';
import { floatingSurface, menuGroupLabel } from './_lib/menuStyles';
import { useControllable } from './_lib/useControllable';
import { IconButton } from './IconButton';
import { Input } from './Input';
import './_lib/motion.css';
import { useLayerStyle } from './_lib/layer';
import { cn } from '../basis/kern';

const COLS = 8;

interface Section {
  id: string;
  title: string;
  emojis: EmojiEntry[];
}

export interface EmojiPanelProps {
  onSelect: (emoji: string) => void;
  /** Zuletzt genutzte Emojis (Zeichen); Standard: eine feste Auswahl. */
  frequent?: string[];
  className?: string;
}

/** Inhalt des Pickers (auch ohne Popover nutzbar, z. B. im mobilen Sheet). */
export function EmojiPanel({ onSelect, frequent = DEFAULT_FREQUENT, className }: EmojiPanelProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const sections = useMemo<Section[]>(() => {
    if (query.trim())
      return [{ id: 'results', title: 'Search results', emojis: searchEmojis(query) }];
    const recent = frequent.map((char) => emojiByChar(char) ?? { char, name: char });
    return [{ id: 'frequent', title: 'Frequently used', emojis: recent }, ...EMOJI_GROUPS].filter(
      (s) => s.emojis.length > 0,
    );
  }, [query, frequent]);
  const flat = useMemo(() => sections.flatMap((s) => s.emojis), [sections]);
  /** Erster globaler Index je Abschnitt. */
  const starts = useMemo(
    () => sections.map((_, i) => sections.slice(0, i).reduce((sum, s) => sum + s.emojis.length, 0)),
    [sections],
  );
  const current = Math.min(active, Math.max(flat.length - 1, 0));

  const focusIndex = (index: number) => {
    setActive(index);
    gridRef.current?.querySelector<HTMLElement>(`[data-emoji-index="${index}"]`)?.focus();
  };

  const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = gridMove(
      sections.map((s) => s.emojis.length),
      COLS,
      current,
      event.key,
    );
    if (next === null) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && next === current) searchRef.current?.focus();
    else focusIndex(next);
  };

  return (
    <div className={cn('flex h-90 w-80 max-w-full flex-col', className)}>
      <div className="p-2 pb-1">
        <Input
          ref={searchRef}
          type="search"
          aria-label="Search emoji"
          placeholder="Search emoji"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' && flat.length) {
              event.preventDefault();
              focusIndex(0);
            }
          }}
        />
      </div>
      {/* Roving-Fokus: Tasten an den Knöpfen, der Container verteilt sie. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={gridRef}
        onKeyDown={onGridKeyDown}
        className="min-h-0 flex-1 overflow-y-auto px-2 pb-2"
      >
        {flat.length === 0 ? (
          <p role="status" className="px-2 py-6 text-center text-label text-fg-secondary">
            No emoji found
          </p>
        ) : (
          sections.map((section, sectionIndex) => {
            const start = starts[sectionIndex] ?? 0;
            return (
              <div key={section.id} role="group" aria-label={section.title}>
                <div aria-hidden="true" className={cn(menuGroupLabel, 'px-1')}>
                  {section.title}
                </div>
                <div className="grid grid-cols-8">
                  {section.emojis.map((emoji, i) => {
                    const index = start + i;
                    return (
                      <button
                        key={`${section.id}-${emoji.char}`}
                        type="button"
                        data-emoji-index={index}
                        tabIndex={index === current ? 0 : -1}
                        aria-label={emoji.name}
                        title={emoji.name}
                        onClick={() => onSelect(emoji.char)}
                        onFocus={() => setActive(index)}
                        className={cn(
                          'inline-flex size-9 items-center justify-center rounded-sm text-kpi',
                          'transition-colors duration-(--bar-dur-fast) ease-standard hover:bg-tile focus-visible:bg-tile',
                          'focus-visible:outline-offset-[-2px]',
                        )}
                      >
                        {emoji.char}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export interface EmojiPickerProps extends EmojiPanelProps {
  /** Auslöser; Standard: runder Ghost-Knopf „Emoji“. */
  trigger?: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  /** Nach der Auswahl schließen (Reaktionen) oder offen lassen (mehrere Emojis in den Text). */
  closeOnSelect?: boolean;
}

export function EmojiPicker({
  trigger,
  open: openProp,
  onOpenChange,
  side = 'top',
  align = 'end',
  closeOnSelect = true,
  onSelect,
  ...panel
}: EmojiPickerProps) {
  const [open, setOpen] = useControllable(openProp, false, onOpenChange);
  const layerStyle = useLayerStyle();
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        {trigger ?? (
          <IconButton
            label="Emoji"
            icon={<SmileyIcon />}
            variant="ghost"
            surface="tile"
            size="sm"
          />
        )}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={16}
          aria-label="Emoji picker"
          style={layerStyle}
          className={cn(floatingSurface, 'overflow-hidden rounded-xl')}
        >
          <EmojiPanel
            {...panel}
            onSelect={(emoji) => {
              onSelect(emoji);
              if (closeOnSelect) setOpen(false);
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
